import { PagesConfigKey } from './pages-config';
import BookmarkButton from './bookmark-button';
import { Link as RouterLink } from 'react-router-dom';
import SharedSvg from './shared-svg';
import { BreadcrumbItem } from './simple-breadcrumbs-page';

import Breadcrumbs from '@mui/material/Breadcrumbs';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import type { Theme } from '@mui/material/styles';

interface SharedPageHeaderProps {
  bookmarkKey?: PagesConfigKey;
  breadcrumbs?: BreadcrumbItem[];
  infoTooltip?: string;
  isSubPage?: boolean;
  headerActions?: React.ReactNode;
}

export default function SharedPageHeader({
  bookmarkKey,
  breadcrumbs,
  infoTooltip,
  isSubPage = false,
  headerActions,
}: SharedPageHeaderProps) {
  const isMainPage = !isSubPage;

  const breadcrumbStyles = {
    fontSize: isMainPage ? 20 : 14,
    separatorSize: isMainPage ? undefined : 20,
    bookmarkSize: isMainPage ? 24 : 14,
    linkColor: (theme: Theme) =>
      theme.palette.mode === 'light'
        ? theme.palette.text.secondary
        : theme.palette.common.white,
  };

  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%', p: 1, pt: 2 }}
    >
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Stack direction="row" alignItems="center" gap={isMainPage ? 1.5 : 0}>
          {isMainPage && <SharedSvg variant="red" width={16} height={16} />}

          <Breadcrumbs
            separator={
              <KeyboardArrowRightOutlined
                sx={
                  breadcrumbStyles.separatorSize
                    ? { fontSize: breadcrumbStyles.separatorSize }
                    : undefined
                }
              />
            }
            sx={{
              '& .MuiBreadcrumbs-ol': {
                alignItems: 'center',
              },
              '& .MuiBreadcrumbs-separator': {
                mx: 1,
              },
            }}
          >
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;

              if (!item.path) {
                return (
                  <Typography
                    key={item.label}
                    onClick={item.onClick}
                    sx={{
                      fontSize: breadcrumbStyles.fontSize,
                      fontWeight: isLast ? 500 : 400,
                      color: (theme) =>
                        theme.palette.mode === 'light'
                          ? isLast
                            ? theme.palette.primary.main
                            : isMainPage
                            ? theme.palette.text.secondary
                            : theme.palette.text.disabled
                          : isLast
                          ? theme.palette.text.primary
                          : isMainPage
                          ? theme.palette.text.secondary
                          : theme.palette.text.disabled,
                      py: 0.25,
                      cursor: item.onClick ? 'pointer' : 'default',
                    }}
                  >
                    {item.label}
                  </Typography>
                );
              }

              return (
                <Link
                  key={item.label}
                  component={RouterLink}
                  to={`/${item.path}`}
                  onClick={item.onClick}
                  underline="hover"
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    py: 0.25,
                    px: 0.75,
                    mx: -0.75,
                    borderRadius: 1,
                    fontSize: breadcrumbStyles.fontSize,
                    fontWeight: 400,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      color: theme.palette.text.primary,
                      textDecoration: 'underline',
                    },
                  })}
                >
                  {item.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" gap={0.5}>
        {infoTooltip && (
          <Tooltip title={infoTooltip}>
            <IconButton size="small">
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        )}
        {bookmarkKey && (
          <BookmarkButton
            bookmarkKey={bookmarkKey}
            sx={{
              width: 24,
              height: 24,
              '& svg': {
                fontSize: breadcrumbStyles.bookmarkSize,
              },
            }}
          />
        )}
        {headerActions}
      </Stack>
    </Stack>
  );
}
